import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "react-bootstrap";
import { Document, Page, pdfjs } from "react-pdf";
import { Rnd } from "react-rnd";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../../firebase/AuthProvider";
import {
  SignatureField,
  SignatureType,
} from "../../../../interfaces/file/fileSignature/SignatureBox";
import "./PDFViewer.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faCheckCircle,
  faFont,
  faICursor,
  faSignature,
} from "@fortawesome/free-solid-svg-icons";
import { cancelSignatureRequestById, deleteSignatureBoxById, getSignatureBoxes } from "../../../../services/fileServices/signatures/SignatureService";
import Loader from "../../loader/Loader";
import ErrorLoadDoc from "./guestSignComponents/ErrorLoadDoc";
import ErrorDocProgressLoader from "./guestSignComponents/ErrorDocProgressLoader";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface PDFViewerProps {
  fileUrl: string;
  containerWidth: number;
  canEdit?: boolean;
  isSigning?: boolean;
  isPlacingBox?: boolean;
  onPlaceBox?: (pageNumber: number, x: number, y: number) => void;
  selectedSignerEmail: string;
  signatureFields: SignatureField[];
  onSignatureFieldsChange?: (fields: SignatureField[]) => void;
  onBoxSelect?: (boxId: string) => void;
  setCanRequestCancel?: React.Dispatch<React.SetStateAction<boolean>>;
  currentPage: number;
  setNumPages: (numPages: number) => void;
  selectedBoxId: string | null;
  initialFieldIds: Set<string>;
  onApiDataLoaded?: (fields: SignatureField[]) => void;
}

const isImage = (value?: string) => {
  if (!value) return false;
  return value.startsWith("data:image/");
};

const isStoredSignaturePath = (value?: string) => {
  if (!value) return false;
  const lower = value.toLowerCase();
  return lower.includes("signatures/") || lower.includes("signature_");
};

const getSignedDisplayText = (field: SignatureField) => {
  const value = field.signatureValue?.trim();
  if (!value) return field.signerEmail || "Signed";
  if (isImage(value) || isStoredSignaturePath(value)) return field.signerEmail || "Signed";
  return value;
};

const PDFViewer: React.FC<PDFViewerProps> = ({
  fileUrl,
  containerWidth,
  canEdit = false,
  isSigning = false,
  isPlacingBox = false,
  onPlaceBox,
  selectedSignerEmail,
  signatureFields,
  onSignatureFieldsChange,
  onBoxSelect,
  setCanRequestCancel,
  currentPage,
  setNumPages,
  selectedBoxId,
  initialFieldIds,
  onApiDataLoaded
}) => {
  const { currentUser } = useAuth();
  const { fileId } = useParams();
  const navigate = useNavigate();
  const localFieldsRef = useRef<SignatureField[]>([]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fieldToDeleteId, setFieldToDeleteId] = useState<string | null>(null);
  const [pdfFileOptions, setPdfFileOptions] = useState<any>(null);
  // key to force re-mounting Document on retry
  const [reloadKey, setReloadKey] = useState(0);

  const [pageSizes, setPageSizes] = useState<{
    [pageNumber: number]: { width: number; height: number };
  }>({});
  const [originalPageSizes, setOriginalPageSizes] = useState<{
    [pageNumber: number]: { width: number; height: number };
  }>({});

  useEffect(() => {
    const setupPdf = async () => {
      if (!fileUrl) return;
      const authToken = currentUser ? await currentUser.getIdToken() : null;
      setPdfFileOptions({
        url: fileUrl,
        httpHeaders: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
    };
    setupPdf();
  }, [fileUrl, currentUser]);

  // const loadSignatureBoxes = useCallback(async () => {
  //   if (!fileId || !currentUser || !onSignatureFieldsChange) return;
  //   const authToken = await currentUser.getIdToken();
  //   const employeeId = (currentUser as any)?.uid;
  //   const boxes = await getSignatureBoxes(authToken, fileId, employeeId);
  //   if (boxes) {
  //     const mappedFields: SignatureField[] = boxes.map((box) => ({
  //       id: box.id,
  //       pageNumber: box.pageNumber,
  //       x: box.x,
  //       y: box.y,
  //       width: box.width,
  //       height: box.height,
  //       signerEmail: box.assignedSignerEmail,
  //       signatureValue: box.signatureValue,
  //       signatureType: box.boxType.name,
  //     }));

  //     if (setCanRequestCancel) {
  //       const hasUnsigned = mappedFields.some(field => !field.signatureValue);
  //       setCanRequestCancel(hasUnsigned);
  //     }
  //     onSignatureFieldsChange(mappedFields);
  //   }
  // }, [fileId, currentUser, setCanRequestCancel, onSignatureFieldsChange]);

  const loadSignatureBoxes = useCallback(async () => {
    if (!fileId || !currentUser || !onApiDataLoaded) return; // Check onApiDataLoaded

    const authToken = await currentUser.getIdToken();
    const employeeId = (currentUser as any)?.uid;
    const boxes = await getSignatureBoxes(authToken, fileId, employeeId);

    if (!boxes) return;

    const apiFields: SignatureField[] = boxes.map((box) => ({
      id: box.id,
      pageNumber: box.pageNumber,
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height,
      signerEmail: box.assignedSignerEmail,
      signatureValue: box.signatureValue,
      signatureType: box.boxType.name,
    }));

    // CRITICAL CHANGE: Use the specific handler for API data
    onApiDataLoaded(apiFields);

    if (setCanRequestCancel) {
      const hasUnsigned = apiFields.some(f => !f.signatureValue);
      setCanRequestCancel(hasUnsigned);
    }
  }, [fileId, currentUser, setCanRequestCancel, onApiDataLoaded]);

  useEffect(() => {
    // BUG WAS HERE: "filter(f => initialFieldIds.has...)" 
    // FIX: Add '!' to keep fields that are NOT in the database.
    const localOnly = signatureFields.filter(f => !initialFieldIds.has(f.id));

    localFieldsRef.current = localOnly;
  }, [signatureFields, initialFieldIds]);

  const onDocumentLoadSuccess = useCallback(async (pdf: any) => {
    setNumPages(pdf.numPages);
    const sizes: { [pageNumber: number]: { width: number; height: number } } = {};
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 1 }); // Get original size
      sizes[i] = { width: viewport.width, height: viewport.height };
    }
    setOriginalPageSizes(sizes);
    // hide any loading indicator after successful load
    setLoading(false);
  }, [setNumPages, setLoading]);

  const onPageRenderSuccess = useCallback(() => {
    if (canvasRef.current) {
      const { clientWidth, clientHeight } = canvasRef.current;
      setPageSizes(prev => ({
        ...prev,
        [currentPage]: { width: clientWidth, height: clientHeight }
      }));
    }
  }, [currentPage]);

  const handleOverlayLeftClick = (
    pageNumber: number,
    e: React.MouseEvent<HTMLDivElement>
  ) => {
    if (!canEdit || !isPlacingBox || e.target !== e.currentTarget) return;

    const originalPageSize = originalPageSizes[pageNumber];
    const renderedPageSize = pageSizes[pageNumber];
    if (!originalPageSize || !renderedPageSize) return;

    const scaleX = renderedPageSize.width / originalPageSize.width;
    const scaleY = renderedPageSize.height / originalPageSize.height;

    const originalX = e.nativeEvent.offsetX / scaleX;
    const originalY = e.nativeEvent.offsetY / scaleY;

    onPlaceBox?.(pageNumber, originalX, originalY);
  };

  const updateFieldPositionAndSize = (
    id: string,
    x: number,
    y: number,
    width: number,
    height: number
  ) => {
    if (!canEdit) return;
    onSignatureFieldsChange?.(
      signatureFields.map((field) =>
        field.id === id ? { ...field, x, y, width, height } : field
      )
    );
  };

  const onDocumentLoadError = useCallback((error: any) => {
    console.error("Failed to load document:", error);
    setLoading(false);
  }, [setLoading]);

  const handleRetry = useCallback(async () => {
    setLoading(true);
    try {
      const authToken = currentUser ? await currentUser.getIdToken() : null;
      setPdfFileOptions({
        url: fileUrl,
        httpHeaders: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      setReloadKey((k) => k + 1);
    } catch (err) {
      console.error("Retry failed:", err);
      setLoading(false);
    }
  }, [currentUser, fileUrl]);

  // const deleteSignatureBox = async (id: string) => {
  //   if (!canEdit || !fileId || !currentUser) return;
  //   onSignatureFieldsChange?.(signatureFields.filter((field) => field.id !== id));
  //   setLoading(true);
  //   const authToken = await currentUser.getIdToken();
  //   const success = await cancelSignatureRequestById(authToken, id);
  //   setLoading(false);
  //   if (success) {
  //     loadSignatureBoxes();
  //   } else {
  //     alert("Failed to cancel the signature request.");
  //   }
  // };

  const deleteSignatureBox = async (id: string) => {
    if (!canEdit || !currentUser) return;

    // A. Local Delete (Pre-Database)
    if (!initialFieldIds.has(id)) {
      onSignatureFieldsChange?.(signatureFields.filter(f => f.id !== id));
      return;
    }

    // B. Database Delete (Secure & Audited)
    setLoading(true);
    try {
      const authToken = await currentUser.getIdToken();

      // CALL SERVICE LAYER (Obfuscated logic)
      const result = await deleteSignatureBoxById(authToken, id);

      if (result.success) {
        // Success: The box is gone, and if the doc was finished, the backend handled submission.
        // We reload to reflect the new state (e.g. if the document status changed).
        await loadSignatureBoxes();
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error("Delete operation failed", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canEdit) {
      loadSignatureBoxes();
    }
  }, [loadSignatureBoxes, canEdit]);

  return (
    <>
      {loading && <ErrorDocProgressLoader />}
      <div
        className="sign_right_body text-center"
        onContextMenu={(e) => {
          if (canEdit) e.preventDefault();
        }}
      >
        {pdfFileOptions && (
          <Document
            key={reloadKey}
            file={pdfFileOptions}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            error={<ErrorLoadDoc onRetry={handleRetry} onGoBack={() => navigate(-1)} />}
            loading={<ErrorDocProgressLoader />}
          >
            <div style={{ position: "relative", display: "inline-block" }}>
              <Page
                pageNumber={currentPage}
                renderTextLayer={false}
                width={containerWidth}
                canvasRef={canvasRef}
                onRenderSuccess={onPageRenderSuccess}
              />
              {pageSizes[currentPage] && originalPageSizes[currentPage] && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: pageSizes[currentPage].width,
                    height: pageSizes[currentPage].height,
                    pointerEvents: (canEdit || isSigning) ? "auto" : "none",
                  }}
                  onClick={(e) => handleOverlayLeftClick(currentPage, e)}
                >
                  {signatureFields
                    .filter((f) => f.pageNumber === currentPage)
                    .map((field) => {
                      const originalPageSize = originalPageSizes[field.pageNumber];
                      const renderedPageSize = pageSizes[field.pageNumber];

                      if (!originalPageSize || !renderedPageSize) return null;

                      const scaleX = renderedPageSize.width / originalPageSize.width;
                      const scaleY = renderedPageSize.height / originalPageSize.height;

                      const scaledPosition = { x: field.x * scaleX, y: field.y * scaleY };
                      const scaledSize = { width: field.width * scaleX, height: field.height * scaleY };

                      const isCurrentUserSigner = selectedSignerEmail && field.signerEmail && field.signerEmail.toLowerCase() === selectedSignerEmail.toLowerCase();
                      return (
                        <Rnd
                          key={field.id}
                          size={scaledSize}
                          position={scaledPosition}
                          onDragStop={(e, data) => {
                            const originalX = data.x / scaleX;
                            const originalY = data.y / scaleY;
                            updateFieldPositionAndSize(field.id, originalX, originalY, field.width, field.height);
                          }}
                          onResizeStop={(_e, _dir, ref, _delta, pos) => {
                            const originalX = pos.x / scaleX;
                            const originalY = pos.y / scaleY;
                            const originalWidth = parseFloat(ref.style.width) / scaleX;
                            const originalHeight = parseFloat(ref.style.height) / scaleY;
                            updateFieldPositionAndSize(field.id, originalX, originalY, originalWidth, originalHeight);
                          }}
                          bounds="parent"
                          disableDragging={!canEdit || !!field.signatureValue}
                          enableResizing={canEdit && !field.signatureValue}
                          onClick={(e: any) => {
                            e.stopPropagation();
                            onBoxSelect?.(field.id);
                          }}
                        >
                          <div
                            className={`relative h-full w-full singed_input flex items-center justify-center ${field.id === selectedBoxId ? "selected bg-opacity-90" : "bg-opacity-40"
                              } ${field.signatureValue
                                ? "border border-green-500 bg-green-200 signed"
                                : "bg-purple-200 text-purple-600"
                              } ${isSigning && isCurrentUserSigner && !field.signatureValue ? "cursor-pointer" : ""
                              }`}
                            title={field.signatureValue || field.signerEmail || "Signature Box"}
                          >
                            {field.signatureValue ? (
                              <FontAwesomeIcon icon={faCheckCircle} className="completed_sign_icon" />
                            ) : (
                              canEdit && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFieldToDeleteId(field.id);
                                    setShowConfirmModal(true);
                                  }}
                                  className="delet_sign_input"
                                >
                                  x
                                </button>
                              )
                            )}
                            <div
                              className={`sing_input_txt flex items-center gap-2 w-full px-2 
                                ${field.signatureValue ? "text-green-600" : "text-purple-600"}`}
                              style={{ fontSize: `${Math.max(8, 12 * scaleY)}px` }}
                            >
                              {field.signatureValue ? (
                                isImage(field.signatureValue) ? (
                                  <img
                                    src={field.signatureValue}
                                    alt="Signature"
                                    className="h-full w-full object-contain"
                                  />
                                ) : (
                                  <span className="inter-font font-semibold p-2">
                                    {/* TEST-{field.signatureValue}
                                     */}
                                    {getSignedDisplayText(field)}
                                  </span>
                                )
                              ) : (
                                isSigning && isCurrentUserSigner ? (
                                  <span className="inter-font font-semibold p-2">Click to Sign</span>
                                ) : (
                                  <>
                                    {field.signatureType === SignatureType.DATE && (
                                      <FontAwesomeIcon icon={faCalendarDays} />
                                    )}
                                    {field.signatureType === SignatureType.SIGNATURE && (
                                      <FontAwesomeIcon icon={faSignature} />
                                    )}
                                    {field.signatureType === SignatureType.INITIALS && (
                                      <FontAwesomeIcon icon={faFont} />
                                    )}
                                    {field.signatureType === SignatureType.TEXT && (
                                      <FontAwesomeIcon icon={faICursor} />
                                    )}
                                    <span
                                      className="inter-font font-medium truncate block"
                                      style={{
                                        maxWidth: "100%",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {field.signerEmail || "No email"}
                                    </span>

                                  </>
                                )
                              )}
                            </div>
                          </div>
                        </Rnd>
                      );
                    })}
                </div>
              )}
            </div>
          </Document>
        )}
      </div>
      {showConfirmModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h2 className="text-lg font-semibold mb-4">Confirm Delete</h2>
            <p className="text-sm mb-6">
              Are you sure you want to delete this signature field?
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                className="btn_sm"
                variant="secondary"
                onClick={() => {
                  setShowConfirmModal(false);
                  setFieldToDeleteId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className="btn_sm"
                variant="primary"
                onClick={async () => {
                  if (fieldToDeleteId) {
                    await deleteSignatureBox(fieldToDeleteId);
                    setShowConfirmModal(false);
                    setFieldToDeleteId(null);
                  }
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PDFViewer;
